/**
 * The New cycle dialog (f10.2).
 *
 * Asks for month and year and NOTHING else -- no code, label, date or previous
 * cycle input (runbook §2, Step 0: "nothing is typed by hand afterwards"). As
 * the choice changes, the server's preview renders what would be derived; the
 * dialog computes none of it.
 */

import { useEffect, useState } from 'react';
import { Check, CalendarClock } from 'lucide-react';

import { ApiError, type CycleDerived } from '@/api/client';
import { generalMessage, problemFor } from '@/api/fieldErrors';
import { useCreateCycle, useCyclePreview } from '@/api/queries';
import { DialogField, Modal } from '@/routes/reference-data/Modal';

import {
  MILESTONE_COLUMNS,
  MONTH_NAMES,
  closedWindowText,
  defaultPeriod,
  formatCycleDate,
  milestoneFor,
  openWindowText,
  workingDayTag,
} from './cycleView';

const PREVIEW_DEBOUNCE_MS = 300;

type Period = { year: number; month: number };

function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

function DerivedSummary({ code, derived }: { code: string; derived: CycleDerived }) {
  const open = openWindowText(derived);
  return (
    <div className="cycle-derived" aria-live="polite">
      <div className="cycle-derived-head">
        <span className="cycle-derived-code mono">{code}</span>
        <div>
          <div className="cycle-derived-label">{derived.label}</div>
          <div className="hint">Closing month {derived.closing_month.label}</div>
        </div>
      </div>

      <dl className="cycle-derived-facts">
        <div>
          <dt>Closed months</dt>
          <dd>{closedWindowText(derived)}</dd>
        </div>
        <div>
          <dt>Open forecast window</dt>
          <dd>{open ?? 'None — the closing month is December'}</dd>
        </div>
        <div>
          <dt>Previous cycle</dt>
          <dd>
            {derived.previous_cycle ? (
              <>
                <span className="mono">{derived.previous_cycle.code}</span> ·{' '}
                {derived.previous_cycle.state.name}
              </>
            ) : (
              'None — this will be the first cycle'
            )}
          </dd>
        </div>
        <div>
          <dt>Compared against</dt>
          <dd>{derived.comparison_label ?? '—'}</dd>
        </div>
      </dl>

      <ol className="cycle-milestones" aria-label="Working-day calendar">
        {MILESTONE_COLUMNS.map((column) => {
          const milestone = milestoneFor(derived, column.key);
          return (
            <li key={column.key}>
              <span className="cycle-milestone-tag">{workingDayTag(milestone)}</span>
              <span className="cycle-milestone-name">{column.label}</span>
              <span className="cycle-milestone-date">
                {milestone ? formatCycleDate(milestone.date) : '—'}
              </span>
            </li>
          );
        })}
      </ol>

      <p className="hint">State: Draft — a new cycle always starts in Draft.</p>
    </div>
  );
}

export function NewCycleDialog({ onClose }: { onClose: () => void }) {
  const [period, setPeriod] = useState<Period>(() => defaultPeriod(new Date()));
  const settled = useDebounced(period, PREVIEW_DEBOUNCE_MS);
  const preview = useCyclePreview(settled);
  const create = useCreateCycle();

  // A new choice clears the refusal of the previous one.
  const { reset } = create;
  useEffect(() => reset(), [period.year, period.month, reset]);

  const existing = preview.data?.existing_cycle ?? null;
  const conflict =
    create.error instanceof ApiError && create.error.reason === 'CYCLE_ALREADY_EXISTS'
      ? create.error.message
      : null;
  const monthProblem =
    problemFor(create.error, 'month') ??
    problemFor(preview.error, 'month') ??
    conflict ??
    (existing
      ? `Cycle ${existing.code} already exists (${existing.state.name}).`
      : null);
  const yearProblem = problemFor(create.error, 'year') ?? problemFor(preview.error, 'year');
  const stale = settled !== period || preview.isFetching;
  const general = generalMessage(create.error, 'month', 'year');

  return (
    <Modal
      title="New cycle"
      subtitle="Choose the closing month. Everything else is derived — nothing is typed twice."
      onClose={onClose}
      onSubmit={() => {
        if (existing || stale) return;
        create.mutate(period, { onSuccess: onClose });
      }}
      busy={create.isPending}
      submitDisabled={existing !== null || stale || preview.isError}
      submitLabel="Create cycle"
      submitIcon={<Check aria-hidden="true" />}
      message={general && general !== conflict ? general : null}
    >
      <DialogField label="Closing month" htmlFor="cycle-month" problem={monthProblem}>
        <select
          id="cycle-month"
          value={period.month}
          onChange={(event) => setPeriod({ ...period, month: Number(event.target.value) })}
        >
          {MONTH_NAMES.map((name, index) => (
            <option key={name} value={index + 1}>
              {name}
            </option>
          ))}
        </select>
      </DialogField>
      <DialogField label="Year" htmlFor="cycle-year" problem={yearProblem}>
        <input
          id="cycle-year"
          type="number"
          inputMode="numeric"
          value={period.year}
          onChange={(event) => setPeriod({ ...period, year: Number(event.target.value) })}
        />
      </DialogField>

      <div className="field span-2">
        {preview.data ? (
          <DerivedSummary code={preview.data.code} derived={preview.data.derived} />
        ) : (
          <div className="cycle-derived cycle-derived-pending">
            <CalendarClock aria-hidden="true" />
            <span className="hint">
              {preview.isError && !yearProblem && !monthProblem
                ? 'The preview could not be loaded.'
                : 'Deriving the cycle…'}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
}
